import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, TrendingUp, CheckCircle2, Sparkles, Download, Send, Search, ExternalLink, ChevronRight } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";
import { fmtDatePT } from "../../lib/time";
import { useUrlState } from "../../lib/useUrlState";
import AttendeeDrawer from "../../components/AttendeeDrawer";

const STATUS_OPTIONS = [
  { k: "all", label: "All attendees" },
  { k: "member", label: "Members" },
  { k: "non_member", label: "Non-members" },
  { k: "warm", label: "Warm (2+ events)" },
  { k: "converted", label: "Converted" },
];

const RANGE_OPTIONS = [
  { k: "30", label: "Last 30 days" },
  { k: "90", label: "Last 90 days" },
  { k: "180", label: "Last 6 months" },
  { k: "365", label: "Last 12 months" },
  { k: "all", label: "All time" },
];

const SOURCE_OPTIONS = [
  { k: "all", label: "All events" },
  { k: "native", label: "Native only" },
  { k: "luma", label: "Luma only" },
];

export default function AdminCommunity() {
  // The whole analytics view is shareable: /admin/community?range=365&source=luma
  // reopens exactly this cut for whoever you send it to (see lib/useUrlState).
  const [range, setRange] = useUrlState("range", "90", { push: true });
  const [source, setSource] = useUrlState("source", "all", { push: true });
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [utm, setUtm] = useState(null);
  const [perEvent, setPerEvent] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [filter, setFilter] = useUrlState("status", "all", { push: true });
  // The attendee search: URL copy drives the fetch (so it is debounced AND
  // shareable), local box keeps typing responsive.
  const [q, setQ] = useUrlState("q", "");
  const [qInput, setQInput] = useState(q);
  const [selected, setSelected] = useState(new Set());
  const [broadcast, setBroadcast] = useState({ open: false, subject: "", body: "", segment: null });
  const [segments, setSegments] = useState([]);
  // Deliberately NOT in the URL: it is an attendee's email address, and query
  // strings end up in logs, referrers and pasted links. The rest of this screen
  // is shareable; the drawer is not.
  const [drawerEmail, setDrawerEmail] = useState(null);

  const rangeParams = useMemo(() => {
    const p = {};
    if (range !== "all") p.days = Number(range);
    if (source !== "all") p.source = source;
    return p;
  }, [range, source]);

  const load = () => {
    api.get("/community/summary", { params: rangeParams }).then((r) => setSummary(r.data));
    api.get("/community/funnel", { params: rangeParams }).then((r) => setFunnel(r.data));
    api.get("/community/per-event", { params: rangeParams }).then((r) => setPerEvent(r.data));
    api.get("/community/timeseries", { params: { ...rangeParams, days: rangeParams.days || 365, group_by: "week" } }).then((r) => setTimeseries(r.data));
    api.get("/community/utm", { params: rangeParams }).then((r) => setUtm(r.data));
    api.get("/community/segments").then((r) => setSegments(r.data.segments || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, source]);
  useEffect(() => {
    const params = { ...rangeParams, member_status: filter === "all" ? undefined : filter, q: q || undefined };
    api.get("/community/attendees", { params }).then((r) => setAttendees(r.data));
  }, [filter, q, rangeParams]);

  // Mirror the box into the URL once typing settles — which also debounces the
  // fetch above. Replaces rather than pushes: a history entry per keystroke
  // would make Back useless.
  useEffect(() => {
    if (qInput === q) return undefined;
    const t = setTimeout(() => setQ(qInput), 300);
    return () => clearTimeout(t);
  }, [qInput, q, setQ]);

  // Back/forward (or a pasted link) moves the URL under us; keep the box in step.
  // No loop: the debounce above skips when the two already agree.
  useEffect(() => { setQInput(q); }, [q]);

  const toggleSel = (email) => { const s = new Set(selected); s.has(email) ? s.delete(email) : s.add(email); setSelected(s); };
  const selectAll = () => setSelected(new Set(attendees.map((a) => a.email)));
  const clearSel = () => setSelected(new Set());

  const sendBroadcast = async () => {
    let emails = [...selected];
    if (broadcast.segment) {
      try {
        const r = await api.get(`/community/segments/${broadcast.segment}/emails`);
        emails = r.data || [];
      } catch { return toast.error("Failed to load segment"); }
    }
    if (!emails.length) return toast.error("Select at least one recipient or a segment");
    if (!broadcast.subject || !broadcast.body) return toast.error("Subject + message required");
    try {
      await api.post("/community/broadcast", { subject: broadcast.subject, body: broadcast.body, emails });
      toast.success(`Broadcast sent to ${emails.length}`);
      setBroadcast({ open: false, subject: "", body: "", segment: null });
      clearSel();
    } catch (e) { toast.error("Failed to send"); }
  };

  const warm = useMemo(() => attendees.filter((a) => !a.is_member && a.event_count >= 2), [attendees]);

  return (
    <AdminShell
      testId="admin-community-page"
      eyebrow="Community"
      title="Events analytics"
      subtitle="Attendance, conversions, sources, and the warmest follow-ups — across native and Luma events."
      actions={(
        <div className="flex gap-2 flex-wrap" data-testid="range-filters">
          <select className="input-field text-sm" value={range} onChange={(e) => setRange(e.target.value)} data-testid="range-select">
            {RANGE_OPTIONS.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
          </select>
          <select className="input-field text-sm" value={source} onChange={(e) => setSource(e.target.value)} data-testid="source-select">
            {SOURCE_OPTIONS.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
          </select>
        </div>
      )}
    >
      {/* Metric cards */}
          {summary && (
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
              <MetricCard label="Total attendees" value={summary.total_attendees} icon={Users} accent="green" testId="metric-total" />
              <MetricCard label="Members" value={summary.members} sub={`${summary.non_members} non-members`} icon={CheckCircle2} accent="purple" testId="metric-split" />
              <MetricCard label="Conversions" value={summary.conversions} sub={`${summary.conversion_rate}% rate`} icon={TrendingUp} accent="peach" testId="metric-conversions" />
              <MetricCard label="Attendance rate" value={`${summary.attendance_rate}%`} icon={Users} accent="green" testId="metric-attendance" />
              <MetricCard label="Events tracked" value={(summary.native_events || 0) + (summary.luma_events || 0)} sub={`${summary.native_events || 0} native · ${summary.luma_events || 0} Luma`} icon={Sparkles} accent="peach" testId="metric-events" />
            </div>
          )}

          {/* Time-series */}
          <TimeSeriesCard data={timeseries} />

          {/* Funnel */}
          {funnel && (
            <div className="glass-panel p-6 mt-6" data-testid="funnel-card">
              <Overline>Non-member → Member conversion funnel</Overline>
              <div className="mt-6 grid grid-cols-3 gap-4">
                {funnel.steps.map((s, i) => {
                  const top = funnel.steps[0].count;
                  const prev = i > 0 ? funnel.steps[i - 1].count : null;
                  const barPct = top ? Math.round(s.count / top * 100) : 0;
                  const stepPct = prev ? Math.round(s.count / prev * 100) : null;
                  const overallPct = top ? Math.round(s.count / top * 100) : 0;
                  let subLabel;
                  if (i === 0) subLabel = "Top of funnel";
                  else if (i === funnel.steps.length - 1) subLabel = `${overallPct}% overall conversion`;
                  else subLabel = `${stepPct}% advance from prev`;
                  return (
                    <div key={s.label} className="relative" data-testid={`funnel-step-${i}`}>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">{s.label}</div>
                      <div className="stat-value text-4xl mt-2">{s.count}</div>
                      <div className="text-xs font-mono text-muted-foreground mt-1">{subLabel}</div>
                      <div className="h-1.5 rounded-full mt-3 bg-secondary overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${barPct}%`, background: ["hsl(var(--vd-peach))", "hsl(var(--vd-purple))", "hsl(var(--vd-green))"][i] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* UTM sources */}
          <UtmCard data={utm} />

          {/* Warm non-members */}
          {warm.length > 0 && (
            <div className="glass-panel p-6 mt-6" data-testid="warm-non-members">
              <div className="flex items-center justify-between">
                <div>
                  <Overline>Warm non-members</Overline>
                  <div className="font-heading text-lg mt-1">Priority follow-ups · {warm.length}</div>
                  <div className="text-xs text-muted-foreground">Non-members who have attended 2+ events.</div>
                </div>
                <button onClick={() => { setSelected(new Set(warm.map((w) => w.email))); setBroadcast({ ...broadcast, open: true }); }} className="btn-primary text-sm" data-testid="warm-broadcast-btn">
                  <Send size={12} /> Broadcast to warm list
                </button>
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-2">
                {warm.slice(0, 6).map((a) => (
                  <div key={a.email} className="p-3 rounded-xl border border-white/60 bg-white/40 flex items-center justify-between" data-testid={`warm-${a.email}`}>
                    <div>
                      <div className="text-sm font-medium">{a.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{a.email} · {a.event_count} events</div>
                    </div>
                    <span className="chip chip-cream text-[10px]">Warm</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attendee directory */}
          <div className="glass-panel p-6 mt-6" data-testid="attendee-directory">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Overline>Attendee directory</Overline>
                <span className="chip chip-cream-2 text-[10px]">{attendees.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <select className="input-field text-sm w-44" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="attendee-filter">
                  {STATUS_OPTIONS.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
                </select>
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input className="input-field pl-8 text-sm w-52" placeholder="Search by name, email…" value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="attendee-search" />
                </div>
                {selected.size > 0 && (
                  <button onClick={() => setBroadcast({ ...broadcast, open: true })} className="btn-primary text-sm" data-testid="broadcast-selected-btn">
                    <Send size={12} /> Broadcast to {selected.size}
                  </button>
                )}
                <button onClick={async () => {
                  try {
                    const r = await api.get("/community/attendees/export", { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([r.data]));
                    const a = document.createElement("a"); a.href = url; a.download = "attendees.csv"; a.click();
                    window.URL.revokeObjectURL(url);
                  } catch { toast.error("Export failed"); }
                }} className="btn-ghost text-sm" data-testid="export-attendees-btn"><Download size={12} />Export</button>
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="text-left py-2 w-8">
                      <input type="checkbox" checked={selected.size === attendees.length && attendees.length > 0} onChange={(e) => e.target.checked ? selectAll() : clearSel()} className="accent-primary" />
                    </th>
                    <th className="text-left py-2 pl-2">Person</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Events</th>
                    <th className="text-left py-2">Last seen</th>
                    <th className="text-left py-2">Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {attendees.length === 0 && <tr><td colSpan="6" className="p-6 text-muted-foreground text-center">No matches.</td></tr>}
                  {attendees.slice(0, 200).map((a) => (
                    <tr key={a.email} onClick={() => setDrawerEmail(a.email)} className="border-t border-white/40 hover:bg-white/50 transition cursor-pointer" data-testid={`attendee-row-${a.email}`}>
                      <td className="py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(a.email)} onChange={() => toggleSel(a.email)} className="accent-primary" />
                      </td>
                      <td className="py-3 pl-2">
                        <div className="font-medium">{a.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{a.email}{a.company ? ` · ${a.company}` : ""}</div>
                      </td>
                      <td className="py-3">
                        {a.is_member ? <span className="chip chip-green text-[10px]">Member</span> : <span className="chip chip-cream text-[10px]">Non-member</span>}
                      </td>
                      <td className="py-3 font-mono">{a.event_count}</td>
                      <td className="py-3 text-xs text-muted-foreground">
                        <div className="font-mono">{fmtDatePT(a.last_event_at, { month: "short", day: "numeric" })}</div>
                        <div className="truncate max-w-[180px]">{a.last_event_title}</div>
                      </td>
                      <td className="py-3">
                        {a.converted && <span className="chip chip-purple text-[10px]">Converted</span>}
                        {!a.is_member && a.event_count >= 2 && <span className="chip chip-cream text-[10px]">Warm</span>}
                        {a.checked_in > 0 && <span className="chip chip-green text-[10px] ml-1">{a.checked_in} check-ins</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {attendees.length > 200 && (
                <div className="text-xs text-muted-foreground mt-2">Showing first 200 of {attendees.length}. Use search or filters to narrow down.</div>
              )}
            </div>
          </div>

          {/* Per-event performance */}
          <div className="glass-panel p-6 mt-6" data-testid="per-event-table">
            <Overline>Per-event performance</Overline>
            <div className="text-xs text-muted-foreground mt-1">Click any event to see the full drilldown.</div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="text-left py-2">Event</th>
                    <th className="text-left py-2">Source</th>
                    <th className="text-left py-2">Regs</th>
                    <th className="text-left py-2">Attendance</th>
                    <th className="text-left py-2">Member mix</th>
                    <th className="text-left py-2">Conversions</th>
                    <th className="text-left py-2">Rating</th>
                    <th className="text-left py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {perEvent.map((e) => (
                    <tr key={e.id} className="border-t border-white/40 hover:bg-white/50 cursor-pointer transition" data-testid={`perevent-${e.id}`}>
                      <td className="py-3">
                        <Link to={`/admin/community/events/${e.id}`} className="block">
                          <div className="font-medium">{e.title}</div>
                          <div className="text-xs text-muted-foreground font-mono">{fmtDatePT(e.start_at, { month: "short", day: "numeric" })}</div>
                        </Link>
                      </td>
                      <td className="py-3">
                        {e.source === "luma" ? (
                          <span className="chip chip-purple text-[10px]">Luma</span>
                        ) : (
                          <span className="chip chip-green text-[10px]">Native</span>
                        )}
                      </td>
                      <td className="py-3 font-mono">{e.registrations}</td>
                      <td className="py-3 font-mono">{e.checked_in}/{e.going} · {e.attendance_rate}%</td>
                      <td className="py-3 text-xs">
                        <span className="chip chip-green text-[10px]">{e.members}M</span>
                        <span className="chip chip-cream text-[10px] ml-1">{e.non_members}NM</span>
                      </td>
                      <td className="py-3">
                        {e.conversions > 0 ? <span className="chip chip-purple text-[10px]">+{e.conversions}</span> : <span className="text-muted-foreground text-xs">0</span>}
                      </td>
                      <td className="py-3 font-mono">{e.avg_rating ?? "—"}<span className="text-muted-foreground text-xs"> ({e.feedback_count})</span></td>
                      <td className="py-3 text-right">
                        <Link to={`/admin/community/events/${e.id}`} className="text-muted-foreground hover:text-foreground" aria-label="Drilldown">
                          <ChevronRight size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        {broadcast.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(20,22,28,0.35)" }} onClick={() => setBroadcast({ ...broadcast, open: false })}>
            <div className="glass-panel p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="broadcast-modal">
              <Overline>Broadcast</Overline>
              <div className="mt-3">
                <div className="overline mb-2">Audience</div>
                <div className="flex flex-wrap gap-1.5" data-testid="segment-picker">
                  <button onClick={() => setBroadcast({ ...broadcast, segment: null })} className={`chip text-[11px] ${!broadcast.segment ? "chip-green" : "chip-cream-2"}`} data-testid="segment-selected">
                    Selected people ({selected.size})
                  </button>
                  {segments.map((s) => (
                    <button key={s.key} onClick={() => setBroadcast({ ...broadcast, segment: s.key })} className={`chip text-[11px] ${broadcast.segment === s.key ? "chip-green" : "chip-cream-2"}`} data-testid={`segment-${s.key}`}>
                      {s.label} · {s.count}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 space-y-3">
                <input className="input-field" placeholder="Subject" value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })} data-testid="broadcast-subject" />
                <textarea rows={5} className="input-field resize-none" placeholder="Your message…" value={broadcast.body} onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })} data-testid="broadcast-body" />
              </div>
              <div className="mt-4 flex gap-2 justify-end">
                <button onClick={() => setBroadcast({ ...broadcast, open: false })} className="btn-ghost">Cancel</button>
                <button onClick={sendBroadcast} className="btn-primary" data-testid="broadcast-send"><Send size={12} />Send</button>
              </div>
            </div>
          </div>
        )}

        {drawerEmail && (
          <AttendeeDrawer
            email={drawerEmail}
            onClose={() => setDrawerEmail(null)}
            onBroadcast={(em) => { setSelected(new Set([em])); setBroadcast({ ...broadcast, open: true }); setDrawerEmail(null); }}
          />
        )}
    </AdminShell>
  );
}

function MetricCard({ label, value, sub, icon: Icon, accent = "green", testId }) {
  const color = { green: "hsl(var(--vd-green))", purple: "hsl(var(--vd-purple-ink))", peach: "hsl(var(--vd-peach-ink))" }[accent];
  const bg = { green: "hsl(var(--vd-green) / 0.14)", purple: "hsl(var(--vd-purple) / 0.24)", peach: "hsl(var(--vd-peach) / 0.14)" }[accent];
  return (
    <div className="glass-panel p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {Icon && <div className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: bg, color }}><Icon size={14} /></div>}
      </div>
      <div className="stat-value text-4xl mt-3">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function TimeSeriesCard({ data }) {
  if (!data) return null;
  const pts = data.points || [];
  if (pts.length === 0) {
    return (
      <div className="glass-panel p-6 mt-6" data-testid="timeseries-card">
        <Overline>Registrations over time</Overline>
        <div className="text-sm text-muted-foreground mt-4">No registrations in this range yet.</div>
      </div>
    );
  }
  const maxReg = Math.max(1, ...pts.map((p) => p.registrations));
  return (
    <div className="glass-panel p-6 mt-6" data-testid="timeseries-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Overline>Registrations over time</Overline>
          <div className="text-xs text-muted-foreground mt-1">Grouped by week · check-ins overlaid</div>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-sm" style={{ background: "hsl(var(--vd-green))" }} /> Registrations</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-sm" style={{ background: "hsl(var(--vd-purple))" }} /> Check-ins</span>
        </div>
      </div>
      <div className="mt-6 flex items-end gap-1.5 h-44">
        {pts.map((p) => {
          const regPct = (p.registrations / maxReg) * 100;
          const checkPct = (p.checkins / maxReg) * 100;
          return (
            <div key={p.bucket} className="flex-1 flex flex-col items-center gap-1 group" title={`${p.bucket}: ${p.registrations} regs · ${p.checkins} check-ins`}>
              <div className="w-full flex flex-col justify-end relative" style={{ height: "160px" }}>
                <div className="absolute inset-x-0 bottom-0 rounded-t-md transition group-hover:opacity-80" style={{ height: `${regPct}%`, background: "hsl(var(--vd-green))", minHeight: p.registrations > 0 ? "3px" : "0" }} />
                <div className="absolute inset-x-0 bottom-0 rounded-t-md" style={{ height: `${checkPct}%`, background: "hsl(var(--vd-purple))", minHeight: p.checkins > 0 ? "2px" : "0", opacity: 0.85 }} />
              </div>
              <div className="text-[9px] text-muted-foreground font-mono truncate w-full text-center">{(p.bucket || "").slice(5)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UtmCard({ data }) {
  if (!data) return null;
  const rows = data.by_source || [];
  const hasAttribution = rows.some((r) => r.key !== "direct");
  return (
    <div className="glass-panel p-6 mt-6" data-testid="utm-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Overline>Traffic sources</Overline>
          <div className="text-xs text-muted-foreground mt-1">Tracked via <span className="font-mono">?utm_source=…&amp;utm_medium=…&amp;utm_campaign=…</span> on public event links.</div>
        </div>
        <div className="text-xs text-muted-foreground">Total: <span className="font-mono">{data.total}</span></div>
      </div>
      {!hasAttribution && (
        <div className="mt-4 p-3 rounded-xl border border-white/60 bg-white/40 text-xs text-muted-foreground">
          No UTM-tagged registrations yet. Append <span className="font-mono text-foreground">?utm_source=linkedin&amp;utm_medium=social&amp;utm_campaign=summer</span> to any share link and future registrations will be attributed here.
        </div>
      )}
      {hasAttribution && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="utm-table">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                <th className="text-left py-2">Source</th>
                <th className="text-left py-2">Registrations</th>
                <th className="text-left py-2">Check-ins</th>
                <th className="text-left py-2">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-white/40" data-testid={`utm-row-${r.key}`}>
                  <td className="py-2 font-mono text-xs">{r.key}</td>
                  <td className="py-2 font-mono">{r.registrations}</td>
                  <td className="py-2 font-mono">{r.checkins}</td>
                  <td className="py-2 font-mono">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 rounded-full bg-secondary overflow-hidden w-24">
                        <div className="h-full" style={{ width: `${r.share}%`, background: "hsl(var(--vd-green))" }} />
                      </div>
                      <span className="text-xs">{r.share}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
